import type { PluginExports } from 'graylog-web-plugin/plugin';

import filterMenuItems, { filterCloudMenuItems } from 'util/conditional/filterMenuItems';
import { IlluminateDotBadge } from 'common/components';
import { DATA_LAKE_ENABLED, DATA_LAKE_PREVIEW_NAV_ITEM, DATA_LAKE_PREVIEW_ENABLED } from 'data-lake/Constants';
import Permissions from 'security-app/Permissions';
import useLicenseCheck from 'license/useLicenseCheck';
import Routes from 'routing/Routes';
import SecurityRoutes from 'security-app/routing/Routes';
import DEFAULT_ENTERPRISE_NAV_ITEM from 'components/navigation/DefaultEnterpriseNavItem';
import { DEFAULT_SECURITY_NAV_ITEM } from 'components/security/bindings';

import EnterpriseRoutes from './EnterpriseRoutes';

const DEFAULT_PERSPECTIVE_SECURITY_PAGES = [
  { path: Routes.SECURITY.ACTIVITY, description: 'Activity', permissions: Permissions.securityApp.read },
  {
    path: SecurityRoutes.SECURITY_EVENTS.ALERTS.LIST,
    description: 'Security Events',
    permissions: 'security_event:read',
  },
  { path: SecurityRoutes.INVESTIGATIONS.LIST, description: 'Investigations', permissions: 'investigations:read' },
  { path: Routes.SECURITY.ANOMALIES, description: 'Anomalies', permissions: 'anomaly_configuration:read' },
  { path: SecurityRoutes.SIGMA.LIST, description: 'Sigma Rules', permissions: 'sigma_rules:read' },
  { path: SecurityRoutes.ASSETS.list('machine'), description: 'Assets', permissions: 'asset:edit' },
];

export const SECURITY_PAGES = [
  { path: Routes.SECURITY.ACTIVITY, description: 'Activity', permissions: Permissions.securityApp.read },
  {
    path: SecurityRoutes.SECURITY_EVENTS.ALERTS.LIST,
    description: 'Security Events',
    permissions: 'security_event:read',
  },
  { path: SecurityRoutes.INVESTIGATIONS.LIST, description: 'Investigations', permissions: 'investigations:read' },
  { path: Routes.DASHBOARDS, description: 'Dashboards' },
  { path: Routes.SECURITY.ANOMALIES, description: 'Anomalies', permissions: 'anomaly_configuration:read' },
  { path: SecurityRoutes.SIGMA.LIST, description: 'Sigma Rules', permissions: 'sigma_rules:read' },
  { path: SecurityRoutes.ASSETS.list('machine'), description: 'Assets', permissions: 'asset:edit' },
  { path: Routes.SEARCH, description: 'Search' },
  ...(DATA_LAKE_ENABLED && DATA_LAKE_PREVIEW_ENABLED ? [DATA_LAKE_PREVIEW_NAV_ITEM] : []),
];

const bindings: PluginExports = {
  navigation: [
    {
      description: DEFAULT_ENTERPRISE_NAV_ITEM.description,
      position: DEFAULT_ENTERPRISE_NAV_ITEM.position,
      children: filterCloudMenuItems(
        filterMenuItems(
          [
            { path: EnterpriseRoutes.OVERVIEW, description: 'Overview', permissions: 'licenseinfos:read' },
            { path: EnterpriseRoutes.ARCHIVES, description: 'Archives', permissions: 'archive:read' },
            { path: EnterpriseRoutes.AUDIT_LOG, description: 'Audit Log', permissions: 'auditlog_status:read' },
            { path: EnterpriseRoutes.LICENSES, description: 'Licenses', permissions: 'licenses:read' },
            { path: EnterpriseRoutes.REPORTS, description: 'Reports' },
            { path: EnterpriseRoutes.CUSTOMIZATION, description: 'Customization', permissions: 'clusterconfig:read' },
            { path: EnterpriseRoutes.FORWARDERS, description: 'Forwarders', permissions: 'forwarders:edit' },
            {
              path: EnterpriseRoutes.ILLUMINATE,
              description: 'Illuminate',
              permissions: 'illuminate_bundle_management:read',
              BadgeComponent: IlluminateDotBadge,
            },
            { path: EnterpriseRoutes.MY_SEARCH_FILTERS, description: 'My Search Filters' },
          ],
          DATA_LAKE_ENABLED ? [] : [EnterpriseRoutes.DATA_LAKE],
        ),
        [EnterpriseRoutes.LICENSES, EnterpriseRoutes.FORWARDERS],
      ),
      BadgeComponent: IlluminateDotBadge,
    },
    {
      description: DEFAULT_SECURITY_NAV_ITEM.description,
      position: DEFAULT_SECURITY_NAV_ITEM.position,
      children: DEFAULT_PERSPECTIVE_SECURITY_PAGES,
      useIsValidLicense: () => {
        const {
          security: { isValid: isValidSecurityLicense },
        } = useLicenseCheck();

        return isValidSecurityLicense;
      },
    },
  ],
};

export default bindings;
